import React from 'react';
import { TimecardDetailData } from '../../shared/types.js';
import { useTimeClock } from '../hooks/useTimeClock';

interface Props {
  reports: TimecardDetailData[];
}

const ReportHeaderCell: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className }) => (
    <div className={`font-bold dark:text-gray-300 pb-1 ${className}`}>{children}</div>
);

const ReportCell: React.FC<{ children: React.ReactNode, className?: string, isMono?: boolean }> = ({ children, className, isMono }) => (
    <div className={`py-1 ${isMono ? 'font-mono' : ''} ${className}`}>{children}</div>
);


const TimecardDetailReport: React.FC<Props> = ({ reports }) => {
    const { companySettings } = useTimeClock();
    if (reports.length === 0) {
        return null;
    }
    const dateRange = reports[0].dateRange;
    const generatedAt = reports[0].generatedAt;
    return (
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 md:p-8 rounded-lg shadow-lg text-black dark:text-white">
            <style>
            {`
                @media print {
                    .print-area {
                        box-shadow: none !important;
                        padding: 0 !important;
                        background-color: transparent !important;
                    }
                    .print-area, .print-area * {
                         color: black !important;
                    }
                    .print-border-b {
                        border-bottom: 1px solid #ccc !important;
                    }
                    .print-underline {
                        text-decoration: underline;
                    }
                    .print-page-break {
                        page-break-after: always;
                    }
                }
            `}
            </style>
            <div className="print-area text-sm">
				<div className="flex justify-between print-text-black dark:">
					<span><strong>Date Range:</strong> {dateRange}</span>
					<span><strong>Printed:</strong> {generatedAt}</span>
				</div>
                <div className="text-center print-text-black">
                    <h2 className="text-2xl font-bold">Detailed Period Report</h2>
                    <p className="text-lg">{companySettings.companyName}</p>
                    <p className="text-sm dark:print-text-black">{companySettings.companyAddress}</p>
                </div>
                {reports.map((data, index) => (
                    <div key={data.employeeName} className={index < reports.length - 1 ? 'print-page-break' : ''}>
                        <div className="my-6 pt-6 print-text-black">
                            <h3 className="text-xl font-bold">{data.employeeName}</h3>
                        </div>
                        <div className="grid grid-cols-9 gap-x-4 text-sm border-b border-current print-text-black">
                            <ReportHeaderCell className="col-span-1">Day of the Week</ReportHeaderCell>
                            <ReportHeaderCell className="col-span-1">Date</ReportHeaderCell>
                            <ReportHeaderCell className="col-span-1 text-right">Start Time</ReportHeaderCell>
                            <ReportHeaderCell className="col-span-1 text-right">Stop Time</ReportHeaderCell>
                            <ReportHeaderCell className="col-span-1">Classification</ReportHeaderCell>
                            <ReportHeaderCell className="col-span-1 text-right">Unpaid</ReportHeaderCell>
                            <ReportHeaderCell className="col-span-1 text-right">Base Time</ReportHeaderCell>
                            <ReportHeaderCell className="col-span-1 text-right">Over Time</ReportHeaderCell>
                            <ReportHeaderCell className="col-span-1 text-right">Total Paid Time</ReportHeaderCell>
                        </div>
                        <div className="text-sm print-text-black">
                            {data.weeks.map((week, weekIndex) => (
                                <div key={weekIndex}>
                                    {week.days.map((day, dayIndex) => (
                                        <div key={dayIndex}>
                                            {day.segments.map((seg, segIndex) => (
                                                <div key={segIndex} className="grid grid-cols-9 gap-x-4">
                                                    <ReportCell className="col-span-1">{seg.day}</ReportCell>
                                                    <ReportCell className="col-span-1">{seg.date}</ReportCell>
                                                    <ReportCell className="col-span-1 text-right" isMono>{seg.startTime}</ReportCell>
                                                    <ReportCell className="col-span-1 text-right" isMono>{seg.stopTime}</ReportCell>
                                                    <ReportCell className="col-span-1">{seg.activity}</ReportCell>
                                                    <ReportCell className="col-span-1 text-right" isMono>{seg.unpaid > 0 ? seg.unpaid.toFixed(2) : ''}</ReportCell>
                                                    <ReportCell className="col-span-1 text-right" isMono>{seg.regular > 0 ? seg.regular.toFixed(2) : ''}</ReportCell>
                                                    <ReportCell className="col-span-1 text-right" isMono>{seg.ot > 0 ? seg.ot.toFixed(2) : ''}</ReportCell>
                                                    <ReportCell className="col-span-1 text-right font-bold" isMono>
                                                        {segIndex === day.segments.length - 1 ? day.totalPaid.toFixed(2) : ''}
                                                    </ReportCell>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                    <div className="grid grid-cols-9 gap-x-4 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 print-border-b">
                                        <ReportCell className="col-start-1 col-span-5 font-bold">Week Ended: {week.weekEnding}</ReportCell>
                                        <ReportCell className="col-start-7 col-span-1 text-right font-bold" isMono>{week.totalRegular.toFixed(2)}</ReportCell>
                                        <ReportCell className="col-span-1 text-right font-bold" isMono>{week.totalOT.toFixed(2)}</ReportCell>
                                        <ReportCell className="col-span-1 text-right font-bold" isMono>{week.totalPaid.toFixed(2)}</ReportCell>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="grid grid-cols-9 gap-x-4 mt-4 pt-2 border-t-2 border-gray-400 dark:border-gray-500 print-border-b">
                            <ReportCell className="col-start-1 col-span-5 font-bold text-lg">Total Hours</ReportCell>
                            <ReportCell className="col-start-7 col-span-1 text-right font-bold text-lg" isMono>{data.grandTotals.regular.toFixed(2)}</ReportCell>
                            <ReportCell className="col-span-1 text-right font-bold text-lg" isMono>{data.grandTotals.ot.toFixed(2)}</ReportCell>
                            <ReportCell className="col-span-1 text-right font-bold text-lg" isMono>{data.grandTotals.paid.toFixed(2)}</ReportCell>
                        </div>
                        <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700 print-border-b print-text-black">
                            <h4 className="font-bold text-lg underline print-underline">Worked Summary</h4>
                            <div className="grid grid-cols-9 gap-x-4 mt-2 text-sm">
                                {Object.keys(data.activitySummary).length > 0 ? (
									Object.entries(data.activitySummary).map(([activity, summary]) => (
										 <React.Fragment key={activity}>
											<ReportCell className="col-start-5 col-span-1">{activity}</ReportCell>
											<ReportCell className="col-span-1 text-right" isMono>{summary.unpaid > 0 ? summary.unpaid.toFixed(2) : ''}</ReportCell>
											<ReportCell className="col-span-1 text-right" isMono>{summary.regular > 0 ? summary.regular.toFixed(2) : ''}</ReportCell>
											<ReportCell className="col-span-1 text-right" isMono>{summary.ot > 0 ? summary.ot.toFixed(2) : ''}</ReportCell>
											<ReportCell className="col-span-1 text-right" isMono>{summary.paid > 0 ? summary.paid.toFixed(2) : ''}</ReportCell>
										 </React.Fragment>
									))
								) : (
                                     <ReportCell className="col-start-5 col-span-1">None</ReportCell>
								)}
                            </div>
                        </div>
                        <div className="mt-4 print-text-black">
                            <h4 className="font-bold text-lg underline print-underline">Leave Summary</h4>
                             <div className="grid grid-cols-9 gap-x-4 mt-2 text-sm">
                                {Object.keys(data.leaveSummary).length > 0 ? (
                                    Object.entries(data.leaveSummary).map(([leaveType, summary]) => (
                                        <React.Fragment key={leaveType}>
                                            <ReportCell className="col-start-5 col-span-1">{leaveType}</ReportCell>
                                            <ReportCell className="col-span-1 text-right" isMono>{summary.unpaid > 0 ? summary.unpaid.toFixed(2) : ''}</ReportCell>
                                            <ReportCell className="col-span-1 text-right" isMono>{summary.regular > 0 ? summary.regular.toFixed(2) : ''}</ReportCell>
                                            <ReportCell className="col-span-1 text-right" isMono>{summary.ot > 0 ? summary.ot.toFixed(2) : ''}</ReportCell>
                                            <ReportCell className="col-span-1 text-right" isMono>{summary.paid > 0 ? summary.paid.toFixed(2) : ''}</ReportCell>
                                        </React.Fragment>
                                    ))
                                ) : (
                                     <ReportCell className="col-start-5 col-span-1">None</ReportCell>
                                )}
                            </div>
                        </div>
                         {index < reports.length - 1 && <hr className="my-8 border-dashed border-gray-300 dark:border-gray-600 print:hidden" />}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TimecardDetailReport;